import React, { useCallback } from 'react';
import { StyleSheet, Platform, View, Pressable, ActionSheetIOS, ActivityIndicator, Appearance } from 'react-native';
import RNPickerSelect from 'react-native-picker-select';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

const isWeb = Platform.OS === 'web';
const isIOS = Platform.OS === 'ios';

export default function SelectInput({
  label,
  subtitle,
  value,
  onValueChange,
  items,
  isRequired,
  isLoading,
  isDisabled,
  error,
  style,
  placeholder,
}) {
  const { colors, theme } = useTheme();

  const resolvedTheme = theme === 'auto'
    ? (Appearance.getColorScheme() === 'dark' ? 'black' : 'white')
    : theme;
  const isDark = isDarkTheme(resolvedTheme);

  const pickerSelectStyles = StyleSheet.create({
    inputIOS: {
      fontSize: 15,
      paddingVertical: 12,
      paddingHorizontal: 12,
      minHeight: 48,
      borderWidth: 1,
      borderColor: error ? colors.color_destructive : colors.border_color_secondary,
      borderRadius: 8,
      color: colors.text_color_primary,
      backgroundColor: colors.solid_color_tertiary,
    },
    inputAndroid: {
      fontSize: 15,
      paddingVertical: 12,
      paddingHorizontal: 12,
      minHeight: 48,
      paddingRight: 36,
      borderWidth: 1,
      borderColor: error ? colors.color_destructive : colors.border_color_secondary,
      borderRadius: 8,
      color: colors.text_color_primary,
      backgroundColor: colors.solid_color_tertiary,
    },
    inputWeb: {
      fontSize: 15,
      paddingVertical: 12,
      paddingHorizontal: 12,
      borderWidth: 0,
      borderRightWidth: 10,
      borderRightColor: 'transparent',
      color: colors.text_color_primary,
      backgroundColor: 'transparent',
      colorScheme: isDark ? 'dark' : 'light',
      width: '100%',
    },
  });

  const styles = StyleSheet.create({
    container: {
      width: '100%',
    },
    labelContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 8,
    },
    subtitle: {
      marginTop: 4,
      lineHeight: 18,
    },
    required: {
      lineHeight: 1,
      marginLeft: 2,
      marginTop: 2,
    },
    loadingContainer: {
      backgroundColor: colors.solid_color_tertiary,
      borderRadius: 8,
      padding: 12,
      justifyContent: 'center',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
    },
    errorContainer: {
      backgroundColor: colors.solid_color_tertiary,
      borderRadius: 8,
      padding: 12,
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: colors.color_destructive,
    },
    error: {
      marginTop: 4,
    },
    webPickerWrapper: {
      backgroundColor: colors.solid_color_tertiary,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: error ? colors.color_destructive : colors.border_color_secondary,
    },
    iosSelectButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 12,
      paddingHorizontal: 12,
      minHeight: 48,
      borderWidth: 1,
      borderColor: error ? colors.color_destructive : colors.border_color_secondary,
      borderRadius: 8,
      backgroundColor: colors.solid_color_tertiary,
    },
    iosSelectLabel: {
      flex: 1,
      marginRight: 8,
    },
  });

  const handleIOSPress = useCallback(() => {
    const options = ['Cancel', ...items.map(item => item.label)];
    ActionSheetIOS.showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex: 0,
        title: placeholder?.label || label || 'Select an option',
        userInterfaceStyle: isDark ? 'dark' : 'light',
      },
      (buttonIndex) => {
        if (buttonIndex > 0) {
          onValueChange(items[buttonIndex - 1].value);
        }
      }
    );
  }, [items, placeholder, label, onValueChange, isDark]);

  const selectedLabel = items.find(item => item.value === value)?.label;
  const displayLabel = selectedLabel || placeholder?.label || 'Select…';

  const renderPicker = () => {
    if (Platform.OS === 'macos') {
      const MacSelectMenu = require('./MacSelectMenu').default;
      return <MacSelectMenu {...{ label, value, onValueChange, items, isDisabled, displayLabel, selectedLabel, styles, colors }} />;
    }
    if (isIOS) {
      return (
        <Pressable
          onPress={handleIOSPress}
          disabled={isDisabled}
          style={styles.iosSelectButton}
        >
          <Text
            size='n'
            color={selectedLabel ? 'primary' : 'secondary'}
            numberOfLines={1}
            style={styles.iosSelectLabel}
          >
            {displayLabel}
          </Text>
          <Icon name="chevron-down" size={12} color={colors.text_color_secondary} />
        </Pressable>
      );
    }

    if (isWeb) {
      return (
        <View style={styles.webPickerWrapper}>
          <RNPickerSelect
            value={value}
            onValueChange={onValueChange}
            items={items}
            style={pickerSelectStyles}
            placeholder={placeholder}
            useNativeAndroidPickerStyle={false}
            disabled={isDisabled}
          />
        </View>
      );
    }

    return (
      <RNPickerSelect
        value={value}
        onValueChange={onValueChange}
        items={items}
        style={pickerSelectStyles}
        placeholder={placeholder}
        useNativeAndroidPickerStyle={false}
        disabled={isDisabled}
      />
    );
  };

  return (
    <View style={[styles.container, style]}>
      {label && (
        <View style={styles.labelContainer}>
          <Text size='n' weight='bold' color='primary'>{label}</Text>
          {isRequired && (
            <Text size='n' color='danger' weight='bold' style={styles.required}>*</Text>
          )}
        </View>
      )}

      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={colors.text_color_secondary} />
        </View>
      ) : error && typeof error === 'string' ? (
        <View style={styles.errorContainer}>
          <Text style={{ color: colors.color_destructive }}>{error}</Text>
        </View>
      ) : (
        renderPicker()
      )}

      {subtitle && (
        <Text size='s' color='secondary' style={styles.subtitle}>{subtitle}</Text>
      )}

      {error && typeof error == 'string' && (
        <Text color='danger' size='n' style={styles.error}>{error}</Text>
      )}
    </View>
  );
}
